import api from './axios';

/**
 * Check if user is authenticated
 * @returns {boolean} Authentication status
 */
export const isAuthenticated = () => {
  return localStorage.getItem(process.env.REACT_APP_JWT_STORAGE_KEY) !== null;
};

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @returns {Promise} Promise object representing the API response
 */
export const register = (userData) => {
  return api.post('/auth/register', userData);
};

/**
 * Log in a user
 * @param {Object} credentials - User login credentials
 * @returns {Promise} Promise object representing the API response
 */
export const login = async (credentials) => {
  const response = await api.post('/auth/login', credentials);
  
  // Store JWT token
  if (response.data.token) {
    localStorage.setItem(process.env.REACT_APP_JWT_STORAGE_KEY, response.data.token);
  }
  
  return response;
};

/**
 * Handle Google OAuth callback
 * @param {string} token - JWT token from Google auth callback
 * @returns {boolean} Success status
 */
export const handleGoogleCallback = (token) => {
  if (token) {
    localStorage.setItem(process.env.REACT_APP_JWT_STORAGE_KEY, token);
    return true;
  }
  return false;
};

/**
 * Log out the current user
 * @returns {Promise} Promise object representing the API response
 */
export const logout = async () => {
  try {
    // API logout call (if server needs to be notified)
    await api.post('/auth/logout');
  } finally {
    // Always remove the token regardless of API response
    localStorage.removeItem(process.env.REACT_APP_JWT_STORAGE_KEY);
  }
};

/**
 * Get current authenticated user
 * @returns {Promise} Promise object representing the API response
 */
export const getCurrentUser = () => {
  return api.get('/auth/me');
};

/**
 * Request password reset
 * @param {Object} data - Reset request data (email)
 * @returns {Promise} Promise object representing the API response
 */
export const requestPasswordReset = (data) => {
  return api.post('/auth/forgot-password', data);
};

/**
 * Reset password with token
 * @param {Object} data - Reset data (token, new password)
 * @returns {Promise} Promise object representing the API response
 */
export const resetPassword = (data) => {
  return api.post('/auth/reset-password', data);
};

/**
 * Get Google OAuth URL for authentication
 * @returns {string} URL for Google OAuth authentication
 */
export const getGoogleAuthUrl = () => {
  // Get the base API URL from the environment
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:6120/api/v1';
  
  // Return the Google OAuth URL
  return `${apiBaseUrl}/auth/google`;
};